/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import '../Css/AgileWorkFlow.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRotateLeft } from '@fortawesome/free-solid-svg-icons';

const AgileWorkflow = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [currentStage, setCurrentStage] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [particles, setParticles] = useState([]);
  const [cornerParticles, setCornerParticles] = useState([]);
  const [isTransitioning, setIsTransitioning] = useState(false); // For overlay
  const buttonRef = useRef(null);
  const sectionRef = useRef(null);

  // Workflow stages data
  const workflowStages = [
    {
      title: 'Planning',
      image: '/images/Agile.png',
      description:
        'Agile Planning Is A Project Management Approach That Emphasizes An Incremental And Iterative Process. It Focuses On Flexibility, Collaboration, And Continuous Improvement, Allowing Teams To Adapt Quickly To Changes And Deliver Value At Every Stage. Through Regular Feedback And Short Development Cycles, It Ensures Better Alignment With Business Goals And Customer Needs.',
    },
    {
      title: 'Design',
      image: '/images/Design.png',
      description:
        'In The Development Process, Every Feature Goes Through Two Parallel Stages: User Experience (UX) And Visual Design. The UX Stage Focuses On Functionality, UsHardability, And Ensuring A Seamless Interaction For The User, While The Visual Stage Emphasizes Aesthetics, Branding, And Overall Look And Feel. By Combining These Two Aspects, We Create Designs That Are Both Intuitive And Visually Engaging, Delivering An Optimal Experience For End Users.',
    },
    {
      title: 'Development',
      image: '/images/Development.png',
      description:
        'Agile Software Development Is A Methodology That Uses An Iterative And Collaborative Approach To Deliver Working Software Efficiently And Reliably. By Breaking Projects Into Smaller, Manageable Cycles, Teams Can Quickly Respond To Changes, Continuously Test Features, And Address Issues Early. This Approach Ensures Faster Delivery, Higher-Quality Software, And Better Alignment With Customer Requirements And Business Goals.',
    },
    {
      title: 'Testing',
      image: '/images/Test.png',
      description:
        'Testing Is A Comprehensive Process Of Evaluating Various Aspects Of The User Experience To Ensure That A Website And Its Features Interact Optimally With The Audience. It Involves Checking Functionality, Usability, Performance, And Compatibility To Identify And Resolve Issues Early, Ensuring A Seamless And Engaging Experience For Users.',
    },
    {
      title: 'Deployment',
      image: '/images/Deployment.png',
      description:
        'Deployment Refers To The Process Of Delivering Applications, Modules, Updates, And Patches From Developers To End Users In A Structured And Controlled Manner. It Ensures That The Software Is Properly Installed, Configured, And Fully Operational In The Target Environment. This Stage Also Includes Monitoring The Deployment For Any Issues, Validating Functionality, And Making Adjustments As Needed To Guarantee A Smooth And Reliable Experience For Users.',
    },
    {
      title: 'Launch',
      image: '/images/Launch.png',
      description:
        'In Agile Methodologies, The Launch Phase Emphasizes Delivering The Right Product That Meets User Needs And Business Goals. It Also Ensures That The Product Is Released At The Optimal Time, Maximizing Impact And User Adoption. This Phase Involves Careful Coordination, Final Testing, And Monitoring Post-Launch Performance To Address Any Issues Promptly And Guarantee A Successful Rollout.',
    },
  ];

  const generateParticles = (count, x, y) => {
    const newParticles = [];
    for (let i = 0; i < count; i++) {
      newParticles.push({
        id: Math.random(),
        x: x || Math.random() * 100,
        y: y || Math.random() * 100,
        size: Math.random() * 4 + 2,
        duration: Math.random() * 1 + 0.5,
        delay: Math.random() * 0.5,
      });
    }
    return newParticles;
  };

  const generateCornerParticles = (count, buttonRect) => {
    if (!buttonRect) return [];

    const newParticles = [];
    for (let i = 0; i < count; i++) {
      const startX = buttonRect.right - 8;
      const startY = buttonRect.bottom - 8;
      const angle = Math.random() * Math.PI / 3 + Math.PI / 6;
      const distance = 80 + Math.random() * 200;

      const endX = startX + Math.cos(angle) * distance;
      const endY = startY + Math.sin(angle) * distance;

      const midX1 = startX + Math.cos(angle) * distance * 0.2;
      const midY1 = startY + Math.sin(angle) * distance * 0.2;
      const midX2 = startX + Math.cos(angle) * distance * 0.5;
      const midY2 = startY + Math.sin(angle) * distance * 0.5;
      const midX3 = startX + Math.cos(angle) * distance * 0.8;
      const midY3 = startY + Math.sin(angle) * distance * 0.8;

      const speedTypes = ['slow', 'medium', 'fast'];
      const speed = speedTypes[Math.floor(Math.random() * speedTypes.length)];

      const shapeTypes = ['circle', 'blob', 'drop'];
      const shape = shapeTypes[Math.floor(Math.random() * shapeTypes.length)];

      const baseColors = [
        'rgba(72, 52, 212, 0.9)',
        'rgba(104, 109, 224, 0.8)',
        'rgba(91, 33, 182, 0.7)',
      ];
      const color = baseColors[Math.floor(Math.random() * baseColors.length)];

      newParticles.push({
        id: Math.random(),
        startX: `${startX}px`,
        startY: `${startY}px`,
        endX: `${endX}px`,
        endY: `${endY}px`,
        midX1: `${midX1}px`,
        midY1: `${midY1}px`,
        midX2: `${midX2}px`,
        midY2: `${midY2}px`,
        midX3: `${midX3}px`,
        midY3: `${midY3}px`,
        size: Math.random() * 5 + 2,
        color: color,
        speed: speed,
        shape: shape,
        opacity: Math.random() * 0.7 + 0.3,
      });
    }
    return newParticles;
  };

  const handleMouseEnter = (e) => {
    if (currentStage < workflowStages.length - 1) {
      setIsHovered(true);
      if (buttonRef.current) {
        const buttonRect = buttonRef.current.getBoundingClientRect();
        setParticles(generateParticles(8, 50, 50));
        setCornerParticles(generateCornerParticles(6, buttonRect));
      }
    }
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
    setTimeout(() => {
      setParticles([]);
      setTimeout(() => setCornerParticles([]), 1000);
    }, 500);
  };

  const handleNextFlow = () => {
    if (currentStage < workflowStages.length - 1) {
      setIsTransitioning(true); // Trigger overlay
      setTimeout(() => {
        setCurrentStage((prev) => prev + 1);
        setIsTransitioning(false); // Reset overlay after transition
      }, 500); // Match overlay transition duration
    }
  };

  const handleRestoreFlow = () => {
    setIsTransitioning(true); // Trigger overlay
    setTimeout(() => {
      setCurrentStage(0);
      setIsTransitioning(false); // Reset overlay after transition
    }, 500); // Match overlay transition duration
  };

  useEffect(() => {
    if (isHovered && buttonRef.current && currentStage < workflowStages.length - 1) {
      const buttonRect = buttonRef.current.getBoundingClientRect();

      const particleInterval = setInterval(() => {
        setParticles((prev) => [...prev, ...generateParticles(2)]);
      }, 200);

      const cornerParticleInterval = setInterval(() => {
        setCornerParticles((prev) => {
          const updatedParticles = prev.filter(
            (p) => Date.now() - p.createdAt < 4000
          );

          const newParticles = generateCornerParticles(2, buttonRect).map(
            (p) => ({
              ...p,
              createdAt: Date.now(),
            })
          );

          return [...updatedParticles, ...newParticles];
        });
      }, 300);

      return () => {
        clearInterval(particleInterval);
        clearInterval(cornerParticleInterval);
      };
    }
  }, [isHovered, currentStage]);

  useEffect(() => {
    const updateParticles = () => {
      if (isHovered && buttonRef.current) {
        const buttonRect = buttonRef.current.getBoundingClientRect();
        setCornerParticles((prev) =>
          prev.map((particle) => ({
            ...particle,
            startX: `${buttonRect.right - 8}px`,
            startY: `${buttonRect.bottom - 8}px`,
          }))
        );
      }
    };

    window.addEventListener('scroll', updateParticles);
    window.addEventListener('resize', updateParticles);

    return () => {
      window.removeEventListener('scroll', updateParticles);
      window.removeEventListener('resize', updateParticles);
    };
  }, [isHovered]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
          }
        });
      },
      {
        threshold: 0.2,
        rootMargin: '0px',
      }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  const currentStageData = workflowStages[currentStage];
  const stageClass = currentStage % 2 === 0 ? 'stage-even' : 'stage-odd';

  return (
    <div className="workflow-section" ref={sectionRef}>
      {/* Overlay for transitions */}
      <div className={`overlay ${isTransitioning ? 'active' : ''}`}></div>

      {currentStage < workflowStages.length - 1 &&
        cornerParticles.map((particle) => (
          <div
            key={particle.id}
            className={`bottom-corner-leak-particle bottom-corner-leak-particle--${particle.speed} bottom-corner-leak-particle--${particle.shape}`}
            style={{
              width: `${particle.size}px`,
              height: `${particle.size}px`,
              background: particle.color,
              left: 0,
              top: 0,
              '--start-x': particle.startX,
              '--start-y': particle.startY,
              '--mid-x1': particle.midX1,
              '--mid-y1': particle.midY1,
              '--mid-x2': particle.midX2,
              '--mid-y2': particle.midY2,
              '--mid-x3': particle.midX3,
              '--mid-y3': particle.midY3,
              '--end-x': particle.endX,
              '--end-y': particle.endY,
              opacity: particle.opacity,
            }}
          />
        ))}

      <div className="container">
        <div className="row">
          <div className="col-12 text-center">
            <h1 className={`section-titles ${isVisible ? 'animate' : ''}`}>
              Know Our <span style={{borderBottom:'5px solid #9374FF'}}>Workflow</span>
            </h1>
            <p className={`intro-text ${isVisible ? 'animate' : ''}`}>
              We follow Agile methodology workflow, it's an iterative approach that helps teams deliver value
              to customers faster and with fewer issues, it is used for project management
              and software development.
            </p>
          </div>
        </div>

        <div className={`workflow-row ${stageClass} mt-5`}>
          <div className={`col-lg-6 justify-content-center align-items-center d-flex order-${currentStage % 2 === 0 ? 1 : 2}`}>
            <img
              src={currentStageData.image}
              alt={currentStageData.title}
              className={`planning-image img-fluid ${isVisible && !isTransitioning ? 'animate' : ''}`}
            />
          </div>

          <div className={`col-lg-6 justify-content-center align-items-center mb-3 d-flex order-${currentStage % 2 === 0 ? 2 : 1}`}>
            <div className={`content-card ${isVisible && !isTransitioning ? 'animate' : ''}`}>
              <h2 className="planning-title text-center">{currentStageData.title}</h2>
              <p className="planning-description">
                {currentStageData.description}
              </p>

              <div className="workflow-navigation justify-content-center align-items-center d-flex mt-4">
                {currentStage < workflowStages.length - 1 && (
                  <button
                    ref={buttonRef}
                    className={`modern-btn ${isHovered ? 'modern-btn--hovered' : ''} `}
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                    onClick={handleNextFlow}
                  >
                    <span className="modern-btn__text">Next Flow</span>
                    <span className="modern-btn__border modern-btn__border--top"></span>
                    <span className="modern-btn__border modern-btn__border--right"></span>
                    <span className="modern-btn__border modern-btn__border--bottom"></span>
                    <span className="modern-btn__border modern-btn__border--left"></span>
                    <div className="modern-btn__particles">
                      {particles.map((particle) => (
                        <span
                          key={particle.id}
                          className="modern-btn__particle"
                          style={{
                            left: `${particle.x}%`,
                            top: `${particle.y}%`,
                            width: `${particle.size}px`,
                            height: `${particle.size}px`,
                            animationDuration: `${particle.duration}s`,
                            animationDelay: `${particle.delay}s`,
                          }}
                        />
                      ))}
                    </div>
                    <span className="modern-btn__glow"></span>
                  </button>
                )}

                {currentStage > 4 && (
                  <button
                    className="modern-btn restore-flow-btn"
                    onClick={handleRestoreFlow}
                  >
                    Restore Flow
                    <FontAwesomeIcon className='mx-2' icon={faRotateLeft} />
                    
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgileWorkflow;